import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  CTGeneralService,
  CtButtonConfiguration,
  CtFormConfiguration,
  CtThemeTypes,
  MAT_RAISED_ACCENT,
  MAT_RAISED_WARN,
  CtModelService,
  CtModelConfiguration,
  CtModelRouteData,
  CtSelectControlValue
} from '@ctsolution/ct-framework';
import { CTMGeneralService } from 'projects/ct-manager/src/app/_core/lib/general.service';
import { ContactRouteData } from '../../../../_core/route-data/contact.route-data';

/**
 * Component per il form principale di edit/create del Contact
 * 
 * Responsabilità:
 * - Gestisce il form con i campi base del contact (Type, Name, DateOfBirth, Role, Title, InternalReference, Active, Note)
 * - Carica la configurazione dal file contact.json
 * - Delega il submit al parent component tramite @Output o submitAll()
 * - Permette il setup dei valori esistenti (modalità edit)
 * 
 * Pattern utilizzato:
 * - Configuration-driven form tramite ct-form
 * - Parent-child communication per submit sincronizzato
 */
@Component({
  selector: 'app-contact-edit-form',
  templateUrl: './contact-edit-form.component.html',
})
export class ContactEditFormComponent implements AfterViewInit {

  /** Event emitter per il submit tradizionale (modalità create o dialog) */
  @Output() onSubmit: EventEmitter<FormGroup | null> = new EventEmitter<FormGroup | null>();
  
  /** Riferimento al componente parent ContactEditComponent per submit sincronizzato */
  @Input() parentComponent: any;

  /** Configurazione del form principale */
  configuration!: CtFormConfiguration;

  /** Flag per indicare se il form è stato inizializzato */
  isFormReady: boolean = false;

  /** Pulsante di cancellazione */
  cancelButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("CT_PAGE.CT_FORM.cancel")
    .setAction(() => this.CTGeneralService.back())
    .setMatherialOptions(MAT_RAISED_WARN);

  /** Pulsante di submit */
  submitButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("CT_PAGE.CT_FORM.save")
    .setClass('ct-model-submit-button')
    .setAction(() => this.handleSubmit())
    .setMatherialOptions(MAT_RAISED_ACCENT);

  constructor(
    private CTGeneralService: CTGeneralService,
    private generalService: CTMGeneralService,
    private modelService: CtModelService<any>,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Inizializza il form caricando la configurazione da contact.json
   */
  async ngAfterViewInit() {
    
    try {
      // Carica la configurazione del form dal file contact.json
      this.configuration = await this.generalService.getFormConfiguration(
        this.formBuilder.group({}),
        ContactRouteData()
      );

      // Marca il form come pronto
      this.isFormReady = true;

      // Force change detection per mostrare il form
      this.cdr.detectChanges();

      console.log('Contact form initialized successfully', this.configuration);
    } catch (error) {
      console.error('Errore durante l\'inizializzazione del form contact:', error);
    }
  }

  /**
   * Setup dei valori del form con i dati esistenti (modalità edit)
   * @param value Dati del contact da caricare nel form
   */
  setup<T>(value: any | null) {
    
    if (!value) return;

    // Assicurati che il form sia pronto prima di popolare i valori
    if (!this.configuration?.form) {
      console.warn('Form non ancora inizializzato, attendo...');
      setTimeout(() => this.setup(value), 100);
      return;
    }

    try {
      // Popola il form con i valori esistenti
      this.configuration.form.patchValue(value);

      if(value.InternalReference && typeof value.InternalReference === 'object' && value.InternalReference.Oid !== undefined) {
        this.configuration.form.get('InternalReference')?.setValue(CtSelectControlValue.create().setValue(value.InternalReference.Oid).setLabel(value.InternalReference.NameSurname));
      }

      console.log('Contact form populated with values', value);
    } catch (error) {
      console.error('Errore durante il setup dei valori del form:', error);
    }
  }

  /**
   * Gestisce il click sul pulsante "Salva"
   * Se siamo in modalità edit (con parentComponent disponibile), chiama il submit sincronizzato
   * Altrimenti, emette l'evento per il submit tradizionale
   */
  handleSubmit(): void {
    
    // Controlla se siamo in modalità edit verificando se esiste un id nel route del parent
    const isEditMode = this.parentComponent?.route?.snapshot?.paramMap?.get('id');
    
    // Se abbiamo il parent component, siamo in edit mode E il metodo submitAll esiste, usa il submit sincronizzato
    if (this.parentComponent && this.parentComponent.submitAll && isEditMode) {
      this.parentComponent.submitAll();
    } else {
      // Altrimenti usa il metodo tradizionale (per create)
      this.onSubmit.emit(this.configuration.form);
    }
  }

}
